import { __, sprintf } from '@wordpress/i18n';
import { useState, useEffect, useCallback } from '@wordpress/element';
import { Button, Spinner, Notice, CheckboxControl } from '@wordpress/components';
import { ImageModal } from './';

const TrashTab = ({ refreshStats }) => {
    const [isLoading, setIsLoading] = useState(false);
    const [items, setItems] = useState([]);
    const [notice, setNotice] = useState(null);
    const [selectedItems, setSelectedItems] = useState([]);
    const [actionLoading, setActionLoading] = useState({});
    const [selectedImage, setSelectedImage] = useState(null);

    // Filter state
    const [searchQuery, setSearchQuery] = useState('');
    const [dateFilter, setDateFilter] = useState('');
    const [bulkAction, setBulkAction] = useState('');
    const [availableDates, setAvailableDates] = useState([]);
    
    // Pagination state
    const [page, setPage] = useState(1);
    const [totalPages, setTotalPages] = useState(1);
    const [totalItems, setTotalItems] = useState(0);
    const limit = 20;

    const fetchTrashItems = useCallback(async () => {
        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_get_trash_items',
                    nonce: window.mtrimAdminData.nonce,
                    page: page,
                    limit: limit,
                    search: searchQuery,
                    mtrim_date: dateFilter
                }),
            });
            const data = await response.json();
            if (data.success) {
                setItems(data.data.items);
                setTotalPages(data.data.pages);
                setTotalItems(data.data.total);
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to fetch trash items', 'media-trim') });
        }
        setIsLoading(false);
    }, [page]);

    useEffect(() => {
        fetchTrashItems();
        setSelectedItems([]); // Reset selection on page change
    }, [page, dateFilter]); // Re-fetch on page or date change

    useEffect(() => {
        fetchAvailableDates();
    }, []);

    const fetchAvailableDates = async () => {
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_get_trash_dates',
                    nonce: window.mtrimAdminData.nonce
                })
            });
            const data = await response.json();
            if (data.success) {
                setAvailableDates(data.data);
            }
        } catch (error) {
            console.error('Error fetching trash dates:', error);
        }
    };

    const handleRestore = async (trashId) => {
        setActionLoading({ ...actionLoading, [trashId]: 'restore' });
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_restore_from_trash',
                    nonce: window.mtrimAdminData.nonce,
                    trash_id: trashId,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                if (refreshStats) refreshStats();
                fetchTrashItems();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to restore item', 'media-trim') });
        }
        setActionLoading({ ...actionLoading, [trashId]: null });
    };

    const handleDeletePermanently = async (trashId) => {
        if (!confirm(__('Are you sure you want to permanently delete this file? This action cannot be undone.', 'media-trim'))) {
            return;
        }

        setActionLoading({ ...actionLoading, [trashId]: 'delete' });
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_delete_permanently',
                    nonce: window.mtrimAdminData.nonce,
                    trash_id: trashId,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                if (refreshStats) refreshStats();
                fetchTrashItems();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to delete item', 'media-trim') });
        }
        setActionLoading({ ...actionLoading, [trashId]: null });
    };

    const handleBulkRestore = async () => {
        if (selectedItems.length === 0) return;
        if (!confirm(sprintf(__('Are you sure you want to restore %d selected items?', 'media-trim'), selectedItems.length))) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_bulk_restore_from_trash',
                    nonce: window.mtrimAdminData.nonce,
                    trash_ids: JSON.stringify(selectedItems),
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                setSelectedItems([]);
                if (refreshStats) refreshStats();
                fetchTrashItems();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to perform bulk restore', 'media-trim') });
        }
        setIsLoading(false);
    };

    const handleBulkDelete = async () => {
        if (selectedItems.length === 0) return;
        if (!confirm(sprintf(__('Are you sure you want to permanently delete %d selected items? This action cannot be undone.', 'media-trim'), selectedItems.length))) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_bulk_delete_permanently',
                    nonce: window.mtrimAdminData.nonce,
                    trash_ids: JSON.stringify(selectedItems),
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                setSelectedItems([]);
                if (refreshStats) refreshStats();
                fetchTrashItems();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to perform bulk deletion', 'media-trim') });
        }
        setIsLoading(false);
    };

    const handleBulkActionApply = () => {
        if (!bulkAction) return;
        if (selectedItems.length === 0) {
            alert(__('Please select some items first.', 'media-trim'));
            return;
        }

        if (bulkAction === 'restore') {
            handleBulkRestore();
        } else if (bulkAction === 'delete') {
            handleBulkDelete();
        }
        setBulkAction('');
    };

    const handleFilterApply = () => {
        setPage(1);
        fetchTrashItems();
    };

    const handleClearFilters = () => {
        setSearchQuery('');
        setDateFilter('');
        setPage(1);
        setTimeout(() => fetchTrashItems(), 0);
    };

    const handleEmptyTrash = async () => {
        if (!confirm(__('Are you sure you want to empty the trash? All items will be permanently deleted.', 'media-trim'))) {
            return;
        }

        setIsLoading(true);
        try {
            const response = await fetch(window.mtrimAdminData.ajaxUrl, {
                method: 'POST',
                headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
                body: new URLSearchParams({
                    action: 'mtrim_empty_trash',
                    nonce: window.mtrimAdminData.nonce,
                }),
            });
            const data = await response.json();
            if (data.success) {
                setNotice({ type: 'success', message: data.data.message });
                if (refreshStats) refreshStats();
                fetchTrashItems();
            } else {
                setNotice({ type: 'error', message: data.data.message });
            }
        } catch (error) {
            setNotice({ type: 'error', message: __('Failed to empty trash', 'media-trim') });
        }
        setIsLoading(false);
    };

    const toggleSelectAll = () => {
        if (selectedItems.length === items.length) {
            setSelectedItems([]);
        } else {
            setSelectedItems(items.map(item => item.id));
        }
    };

    const toggleSelectItem = (id) => {
        if (selectedItems.includes(id)) {
            setSelectedItems(selectedItems.filter(i => i !== id));
        } else {
            setSelectedItems([...selectedItems, id]);
        }
    };

    const renderPagination = () => {
        if (totalPages <= 1) return null;

        const pages = [];
        if (totalPages <= 7) {
            for (let i = 1; i <= totalPages; i++) pages.push(i);
        } else {
            if (page <= 4) {
                pages.push(1, 2, 3, 4, 5, '...', totalPages);
            } else if (page >= totalPages - 3) {
                pages.push(1, '...', totalPages - 4, totalPages - 3, totalPages - 2, totalPages - 1, totalPages);
            } else {
                pages.push(1, '...', page - 1, page, page + 1, '...', totalPages);
            }
        }

        return (
            <div className="mtrim-pagination-wrapper">
                <div className="mtrim-pagination">
                    <button 
                        className="mtrim-pagination-btn"
                        disabled={page === 1} 
                        onClick={() => setPage(page - 1)}
                        title={__('Previous Page', 'media-trim')}
                    >
                        <span className="dashicons dashicons-arrow-left-alt2"></span>
                    </button>
                    
                    {pages.map((p, index) => (
                        p === '...' ? (
                            <span key={`ellipsis-${index}`} className="mtrim-pagination-btn is-ellipsis">...</span>
                        ) : (
                            <button 
                                key={p}
                                className={`mtrim-pagination-btn ${page === p ? 'is-active' : ''}`}
                                onClick={() => setPage(p)}
                            >
                                {p}
                            </button>
                        )
                    ))}

                    <button 
                        className="mtrim-pagination-btn"
                        disabled={page === totalPages} 
                        onClick={() => setPage(page + 1)}
                        title={__('Next Page', 'media-trim')}
                    >
                        <span className="dashicons dashicons-arrow-right-alt2"></span>
                    </button>
                </div>
                <div className="mtrim-page-info">
                    {sprintf(__('Page %d of %d', 'media-trim'), page, totalPages)}
                    <span className="mtrim-separator">|</span>
                    {sprintf(__('Total %d items', 'media-trim'), totalItems)}
                </div>
            </div>
        );
    };

    return (
        <div className="mtrim-results-tab">
            <div className="mtrim-results-header">
                <h2>{__('Trash bin', 'media-trim')} <span>({totalItems})</span></h2>
                <div className="mtrim-results-actions">
                    <Button 
                        isSecondary
                        onClick={fetchTrashItems} 
                        disabled={isLoading}
                        icon="update"
                    />
                    <Button 
                        isDestructive 
                        onClick={handleEmptyTrash}
                        disabled={totalItems === 0 || isLoading}
                    >
                        {__('Empty Trash', 'media-trim')}
                    </Button>
                </div>
            </div>

            <div className="mtrim-filter-area">
                <div className="mtrim-filter-left">
                    <div className="mtrim-filter-group">
                        <select 
                            value={bulkAction} 
                            onChange={(e) => setBulkAction(e.target.value)}
                            className="mtrim-filter-select"
                        >
                            <option value="">{__('Bulk Actions', 'media-trim')}</option>
                                <option value="restore">{__('Restore', 'media-trim')}</option>
                            <option value="delete">{__('Delete Permanently', 'media-trim')}</option>
                        </select>
                        <Button isSecondary onClick={handleBulkActionApply}>
                            {__('Apply', 'media-trim')}
                        </Button>
                    </div>

                    <div className="mtrim-filter-group">
                        <select 
                            value={dateFilter} 
                            onChange={(e) => setDateFilter(e.target.value)}
                            className="mtrim-filter-select"
                        >
                            <option value="">{__('All dates', 'media-trim')}</option>
                            {availableDates.map(date => (
                                <option key={date.value} value={date.value}>{date.label}</option>
                            ))}
                        </select>
                        <Button isSecondary onClick={handleFilterApply}>
                            {__('Filter', 'media-trim')}
                        </Button>
                    </div>

                    {(searchQuery || dateFilter) && (
                        <Button isLink onClick={handleClearFilters}>
                            {__('Clear', 'media-trim')}
                        </Button>
                    )}
                </div>
                <div className="mtrim-filter-right">
                    {selectedItems.length > 0 && (
                        <div className="mtrim-selection-info">
                            <span>{sprintf(__('%d selected', 'media-trim'), selectedItems.length)}</span>
                            <Button isLink onClick={() => setSelectedItems([])}>{__('Clear', 'media-trim')}</Button>
                        </div>
                    )}
                    <input 
                        type="search" 
                        placeholder={__('Search trash...', 'media-trim')}
                        value={searchQuery}
                        onChange={(e) => setSearchQuery(e.target.value)}
                        onKeyDown={(e) => e.key === 'Enter' && handleFilterApply()}
                    />
                    <Button isSecondary onClick={handleFilterApply}>
                        {__('Search', 'media-trim')}
                    </Button>
                </div>
            </div>

            {notice && (
                <Notice 
                    status={notice.type} 
                    isDismissible={true}
                    onRemove={() => setNotice(null)}
                    style={{ marginBottom: '20px' }}
                >
                    {notice.message}
                </Notice>
            )}


            {isLoading && items.length === 0 ? (
                <div className="mtrim-loading-state">
                    <Spinner />
                    <p>{__('Loading trash...', 'media-trim')}</p>
                </div>
            ) : items.length === 0 ? (
                <div className="mtrim-empty-state">
                    <span className="dashicons dashicons-trash"></span>
                    <p>{__('Trash is empty.', 'media-trim')}</p>
                </div>
            ) : (
                <div className="mtrim-table-container">
                    <table className="mtrim-results-table">
                        <thead>
                            <tr>
                                <th className="mtrim-column-checkbox">
                                    <CheckboxControl
                                        checked={selectedItems.length === items.length && items.length > 0}
                                        onChange={toggleSelectAll}
                                    />
                                </th>
                                <th>{__('Preview', 'media-trim')}</th>
                                <th>{__('File Name', 'media-trim')}</th>
                                <th>{__('Size', 'media-trim')}</th>
                                <th>{__('Trashed Date', 'media-trim')}</th>
                                <th>{__('Actions', 'media-trim')}</th>
                            </tr>
                        </thead>
                        <tbody>
                            {items.map(item => (
                                <tr key={item.id} className={selectedItems.includes(item.id) ? 'is-selected' : ''}>
                                    <td>
                                        <CheckboxControl
                                            checked={selectedItems.includes(item.id)}
                                            onChange={() => toggleSelectItem(item.id)}
                                        />
                                    </td>
                                    <td>
                                        <div 
                                            className="mtrim-media-preview" 
                                            onClick={() => item.is_image && setSelectedImage(item.url)}
                                            style={{ cursor: item.is_image ? 'pointer' : 'default' }}
                                        >
                                            {item.is_image ? <img src={item.url} alt="" /> : <span className="dashicons dashicons-media-default"></span>}
                                        </div>
                                    </td>
                                    <td className="mtrim-column-filename">
                                        <strong>{item.file_name}</strong>
                                        <code>{item.original_path}</code>
                                    </td>
                                    <td>{item.formatted_size}</td>
                                    <td>{item.trashed_date}</td>
                                    <td className="mtrim-actions-cell">
                                        <div className="mtrim-row-actions">
                                            <Button 
                                                isSecondary 
                                                icon="undo"
                                                onClick={() => handleRestore(item.id)}
                                                disabled={actionLoading[item.id]}
                                                title={__('Restore', 'media-trim')}
                                            >
                                                {actionLoading[item.id] === 'restore' && <Spinner />}
                                            </Button>
                                            <Button 
                                                isDestructive 
                                                icon="no"
                                                onClick={() => handleDeletePermanently(item.id)}
                                                disabled={actionLoading[item.id]}
                                                title={__('Delete Permanently', 'media-trim')}
                                            >
                                                {actionLoading[item.id] === 'delete' && <Spinner />}
                                            </Button>
                                        </div>
                                    </td>
                                </tr>
                            ))}
                        </tbody>
                    </table>
                    {renderPagination()}
                </div>
            )}

            {selectedImage && (
                <ImageModal 
                    imageUrl={selectedImage} 
                    onClose={() => setSelectedImage(null)} 
                />
            )}
        </div>
    );
};

export default TrashTab;
